"use client";

import { useMemo, useState } from "react";
import { HiClipboardCopy, HiCheck, FaXTwitter } from "@/components/icons";
import ToolsCustomDevCTA from "@/components/ToolsCustomDevCTA";
import UnifiedFAQ from "@/components/UnifiedFAQ";
import ToolPageSchema from "@/components/schemas/ToolPageSchema";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";

const FREE_LIMIT = 280;
const PREMIUM_LIMIT = 25_000;

/**
 * Weighted character count matching X's documented algorithm:
 * - Most code points count as 1.
 * - CJK ideographs (Hangul, Hiragana, Katakana, Han) and visible emoji
 *   (Extended_Pictographic) count as 2.
 * - URLs are shortened to 23 chars by X regardless of length — we apply
 *   the same shortening so the count matches what users see in the composer.
 *
 * This is the practical approximation of twitter-text v3 weighted_count. Not
 * a perfect emulation (no emoji sequence joining edge cases) but close enough
 * for a "will this fit" tool.
 */
const URL_REGEX = /https?:\/\/\S+/g;
const T_CO_URL_LENGTH = 23;

const HEAVY_CODEPOINT_RANGES: ReadonlyArray<readonly [number, number]> = [
  [0x1100, 0x115f], // Hangul Jamo
  [0x2e80, 0x303e], // CJK Radicals through CJK Symbols (excl. punctuation)
  [0x3041, 0x33ff], // Hiragana, Katakana, CJK
  [0x3400, 0x4dbf], // CJK Extension A
  [0x4e00, 0x9fff], // CJK Unified Ideographs
  [0xa000, 0xa4cf], // Yi Syllables
  [0xac00, 0xd7a3], // Hangul Syllables
  [0xf900, 0xfaff], // CJK Compatibility Ideographs
  [0xfe30, 0xfe4f], // CJK Compatibility Forms
  [0xff00, 0xff60], // Fullwidth Forms
  [0xffe0, 0xffe6], // Fullwidth Signs
];

function isHeavyCodePoint(cp: number): boolean {
  for (const [lo, hi] of HEAVY_CODEPOINT_RANGES) {
    if (cp >= lo && cp <= hi) return true;
  }
  return false;
}

function countWeightedChars(text: string): number {
  // Replace each URL with a 23-char placeholder before counting
  const normalized = text.replace(URL_REGEX, "x".repeat(T_CO_URL_LENGTH));

  let count = 0;
  if (typeof Intl !== "undefined" && "Segmenter" in Intl) {
    const seg = new Intl.Segmenter(undefined, { granularity: "grapheme" });
    for (const { segment } of seg.segment(normalized)) {
      const cp = segment.codePointAt(0);
      if (cp === undefined) continue;
      // Visible emoji as a grapheme → 2
      if (/\p{Extended_Pictographic}/u.test(segment)) {
        count += 2;
      } else if (isHeavyCodePoint(cp)) {
        count += 2;
      } else {
        count += 1;
      }
    }
    return count;
  }

  // Fallback: per code point, not perfect for ZWJ emoji sequences
  for (const char of normalized) {
    const cp = char.codePointAt(0);
    if (cp === undefined) continue;
    if (isHeavyCodePoint(cp) || /\p{Extended_Pictographic}/u.test(char)) {
      count += 2;
    } else {
      count += 1;
    }
  }
  return count;
}

function splitIntoThread(text: string, perTweetLimit: number = FREE_LIMIT): string[] {
  if (!text.trim()) return [];
  if (countWeightedChars(text) <= perTweetLimit) return [text];

  // Reserve 7 chars per tweet for " (1/N)" suffix
  const suffixBudget = 7;
  const effective = perTweetLimit - suffixBudget;

  const sentences = text.split(/(?<=[.!?])\s+/);
  const tweets: string[] = [];
  let current = "";

  for (const sentence of sentences) {
    const candidate = current ? `${current} ${sentence}` : sentence;
    if (countWeightedChars(candidate) <= effective) {
      current = candidate;
      continue;
    }
    if (current) tweets.push(current);
    // sentence alone may still exceed; split by words
    if (countWeightedChars(sentence) <= effective) {
      current = sentence;
    } else {
      const words = sentence.split(/\s+/);
      current = "";
      for (const word of words) {
        const next = current ? `${current} ${word}` : word;
        if (countWeightedChars(next) <= effective) {
          current = next;
        } else {
          if (current) tweets.push(current);
          current = word;
        }
      }
    }
  }
  if (current) tweets.push(current);

  const total = tweets.length;
  return tweets.map((t, i) => `${t} (${i + 1}/${total})`);
}

const FAQ_ITEMS = [
  {
    question: "Why does my emoji count as 2 characters?",
    answer:
      "X uses a weighted character count. Most Latin characters count as 1, but emojis and CJK characters (Japanese, Chinese, Korean) count as 2. This matches how X displays the counter in the official composer. The tool applies the same weighting so what you see here matches what you'll see in X.",
  },
  {
    question: "How is the URL handled in the count?",
    answer:
      "X auto-wraps every URL to a t.co short link of exactly 23 characters, regardless of the original URL length. The tool applies the same 23-character substitution before counting, so pasting a long URL counts as 23 — which is what X will count too.",
  },
  {
    question: "What's the 25,000 character limit?",
    answer:
      "X Premium (formerly Twitter Blue) raises the per-post limit from 280 to 25,000 characters. The tool tracks both limits so you can see where you stand for either tier.",
  },
  {
    question: "Does the thread split preview match the X composer?",
    answer:
      "The preview is a heuristic — it splits at sentence boundaries and falls back to word boundaries when a sentence won't fit. X's native thread composer uses a similar approach but may pick different break points. Use the preview to estimate post count and tighten copy; final composition still happens in X.",
  },
  {
    question: "Are my drafts sent anywhere?",
    answer:
      "No. All counting happens in your browser. Nothing is sent to any server, logged, or stored. You can verify this in the browser's network tab — there are no outbound calls.",
  },
];

function ProgressBar({ used, limit }: { used: number; limit: number }) {
  const pct = Math.min((used / limit) * 100, 100);
  const overLimit = used > limit;
  const nearLimit = used > limit * 0.85;
  const color = overLimit ? "bg-red-500" : nearLimit ? "bg-yellow-500" : "bg-green-500";
  return (
    <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
      <div className={`h-full ${color} transition-all`} style={{ width: `${pct}%` }} />
    </div>
  );
}

export default function TweetCharacterCounterClient() {
  const [text, setText] = useState("");
  const [copied, setCopied] = useState<string | null>(null);

  const weighted = useMemo(() => countWeightedChars(text), [text]);
  const codePoints = useMemo(() => Array.from(text).length, [text]);
  const bytes = useMemo(
    () =>
      typeof TextEncoder !== "undefined" ? new TextEncoder().encode(text).length : text.length,
    [text]
  );
  const urls = useMemo(() => (text.match(URL_REGEX) ?? []).length, [text]);
  const thread = useMemo(() => splitIntoThread(text), [text]);
  const overFree = weighted > FREE_LIMIT;
  const overPremium = weighted > PREMIUM_LIMIT;

  const copy = async (content: string, key: string) => {
    await navigator.clipboard.writeText(content);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="container px-4 py-12 mx-auto max-w-4xl">
      <ToolPageSchema
        name="Tweet Character Counter"
        description="Live character counter for X/Twitter. Tracks the 280 free limit and 25,000 Premium limit, weights emojis and CJK characters like X does, and previews thread splits."
        url="https://plugmonkey.xyz/tool/tweet-character-counter/"
        breadcrumbs={[
          { name: "Home", url: "https://plugmonkey.xyz/" },
          { name: "Free Tools", url: "https://plugmonkey.xyz/tools/" },
          {
            name: "Tweet Character Counter",
            url: "https://plugmonkey.xyz/tool/tweet-character-counter/",
          },
        ]}
        faq={FAQ_ITEMS}
      />

      <div className="flex items-center gap-4 mb-8 mt-6">
        <div className="p-3 bg-black/10 dark:bg-white/10 rounded-full">
          <FaXTwitter className="w-8 h-8" />
        </div>
        <div>
          <h1 className="text-3xl md:text-4xl font-bold">Tweet Character Counter</h1>
          <p className="text-muted-foreground mt-1">
            Live counter matching X&apos;s weighted character algorithm. 280 free, 25,000 Premium.
          </p>
        </div>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Your Post</CardTitle>
          <CardDescription>
            Paste or type. Emojis and CJK characters count as 2, URLs count as 23.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <textarea
            id="tweet-text"
            name="tweet-text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="What's happening?"
            rows={6}
            className="w-full px-4 py-3 border border-border rounded-md bg-background font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 resize-y"
            aria-label="Tweet text"
          />
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => copy(text, "all")} disabled={!text}>
              {copied === "all" ? (
                <HiCheck className="w-4 h-4" />
              ) : (
                <HiClipboardCopy className="w-4 h-4" />
              )}
              {copied === "all" ? "Copied" : "Copy"}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setText("")} disabled={!text}>
              Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        <Card className={overFree ? "border-red-500/50" : ""}>
          <CardContent className="p-5 space-y-3">
            <div className="flex items-baseline justify-between">
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground font-mono">
                  Free (280)
                </div>
                <div className="text-3xl font-bold mt-1">
                  {weighted}
                  <span className="text-base font-normal text-muted-foreground">/{FREE_LIMIT}</span>
                </div>
              </div>
              <div
                className={`text-sm font-mono ${overFree ? "text-red-500" : "text-muted-foreground"}`}
              >
                {overFree ? `+${weighted - FREE_LIMIT} over` : `${FREE_LIMIT - weighted} left`}
              </div>
            </div>
            <ProgressBar used={weighted} limit={FREE_LIMIT} />
          </CardContent>
        </Card>

        <Card className={overPremium ? "border-red-500/50" : ""}>
          <CardContent className="p-5 space-y-3">
            <div className="flex items-baseline justify-between">
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground font-mono">
                  Premium (25,000)
                </div>
                <div className="text-3xl font-bold mt-1">
                  {weighted}
                  <span className="text-base font-normal text-muted-foreground">
                    /{PREMIUM_LIMIT.toLocaleString()}
                  </span>
                </div>
              </div>
              <div
                className={`text-sm font-mono ${overPremium ? "text-red-500" : "text-muted-foreground"}`}
              >
                {overPremium
                  ? `+${(weighted - PREMIUM_LIMIT).toLocaleString()} over`
                  : `${(PREMIUM_LIMIT - weighted).toLocaleString()} left`}
              </div>
            </div>
            <ProgressBar used={weighted} limit={PREMIUM_LIMIT} />
          </CardContent>
        </Card>
      </div>

      <Card className="mb-6">
        <CardContent className="p-5">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide font-mono">
                Weighted
              </div>
              <div className="text-xl font-semibold font-mono">{weighted}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide font-mono">
                Code points
              </div>
              <div className="text-xl font-semibold font-mono">{codePoints}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide font-mono">
                UTF-8 bytes
              </div>
              <div className="text-xl font-semibold font-mono">{bytes}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide font-mono">
                URLs
              </div>
              <div className="text-xl font-semibold font-mono">{urls}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {thread.length > 1 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Thread Preview ({thread.length} posts)</CardTitle>
            <CardDescription>
              Each post stays under {FREE_LIMIT} weighted chars including the (n/N) counter.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {thread.map((tweet, i) => (
              <div
                key={i}
                className="border border-border rounded-md p-3 bg-muted/30 font-mono text-sm whitespace-pre-wrap"
              >
                <div className="flex justify-between gap-2 mb-2">
                  <span className="text-xs text-muted-foreground">
                    Post {i + 1} · {countWeightedChars(tweet)} chars
                  </span>
                  <button
                    type="button"
                    onClick={() => copy(tweet, `t-${i}`)}
                    className="text-xs text-primary hover:underline"
                  >
                    {copied === `t-${i}` ? "Copied" : "Copy"}
                  </button>
                </div>
                {tweet}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <div className="mt-16">
        <ToolsCustomDevCTA />
      </div>

      <div className="mt-16">
        <h2 className="text-2xl font-bold mb-6">Frequently Asked Questions</h2>
        <UnifiedFAQ items={FAQ_ITEMS} showSupportLink={false} className="space-y-4" />
      </div>
    </div>
  );
}
